import usePagination from "src/customHooks/usePagination";
import PageHeader from "src/components/pageHeader";
import Pagination from "src/components/pagination";
import PageLoading from "src/components/pageLoading";
import { AiSession } from "../types/ai-session.entity";
import { useCountAISessionsQuery, useGetAISessionsQuery, AISessionsFilter } from "../ai-sessions.service";
import AISessionsTable from "../components/ai-sessions-table";
import AISessionsSearch from "../components/ai-sessions-search";
import { useCallback } from "react";
import { usePaginatedTableSearchParams } from "src/customHooks/usePaginatedTableSearchParams";

const AISessionsPage = () => {
  const tableSearch = usePaginatedTableSearchParams<AISessionsFilter>({
    parseState: (searchParams) => {
      const query: AISessionsFilter = {};
      const userId = searchParams.get("userId");
      const aiSessionId = searchParams.get("aiSessionId");
      const status = searchParams.get("status");

      if (userId) query.userId = userId;
      if (aiSessionId) query.aiSessionId = aiSessionId;
      if (status) query.status = status as AISessionsFilter["status"];

      return query;
    },
    serializeState: (state) => {
      const nextParams = new URLSearchParams();
      if (state.userId) nextParams.set("userId", state.userId);
      if (state.aiSessionId) nextParams.set("aiSessionId", state.aiSessionId);
      if (state.status) nextParams.set("status", state.status);
      return nextParams;
    },
  });

  const initialQuery = tableSearch.state;

  const { data, numberOfPages, currentPage, next, prev, isSuccess, isError, isLoading, isFetching } =
    usePagination<AiSession & { id: string }>({
      useGetDataQuery: useGetAISessionsQuery,
      useCountData: useCountAISessionsQuery,
      initQuery: initialQuery,
      initPageTokens: tableSearch.pageTokens,
      initCurrentIndex: tableSearch.currentIndex,
      syncKey: tableSearch.syncKey,
      onPaginationStateChange: tableSearch.replacePagination,
    });
  const updateQuery = useCallback((newQuery: AISessionsFilter) => {
    tableSearch.replaceFilters(newQuery);
  }, [tableSearch]);

  return (
    <div className="page_container">
      <PageHeader text="AI Sessions" />
      <div className="mb-4">
        <AISessionsSearch updateQuery={updateQuery} initialQuery={initialQuery} />
      </div>
      {isSuccess ? (
        <>
          <AISessionsTable data={data} />
          <Pagination numberOfPages={numberOfPages} currentPage={currentPage} prev={prev} next={next} isLoading={isLoading || isFetching} />
        </>
      ) : isError ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-semibold text-red-800 mb-2">Error Loading AI Sessions</h3>
          <p className="text-red-600">There was an error fetching the AI sessions data. Please check the console for more details.</p>
        </div>
      ) : (
        <PageLoading />
      )}
    </div>
  );
};

export default AISessionsPage;
